import type { CommentableEntityType, CommentType } from '@/types/comments';

export interface CommentListFilters {
  readonly commentType?: CommentType;
  readonly includeResolved?: boolean;
  readonly includeDeleted?: boolean;
}

type CommentFilters = CommentListFilters;

/**
 * Canonical serialization of a (commentable_type, commentable_id, thread_root)
 * tuple. Used by the batch read-receipts query key, the in-hook structural
 * fingerprint, and the realtime patch's targetKey lookup so that all three
 * stay byte-identical. JSON encoding is used so the format is collision-proof
 * even if a future commentable id contains a delimiter character.
 * `thread_root_comment_id` is NOT NULL in the schema; the type is `string`
 * (not optional) to prevent future drift back into the `undefined` vs `''`
 * footgun.
 */
export interface BatchTarget {
  readonly commentableType: CommentableEntityType;
  readonly commentableId: string;
  readonly threadRootCommentId: string;
}

export function serializeBatchTarget(target: BatchTarget): string {
  return JSON.stringify([target.commentableType, target.commentableId, target.threadRootCommentId]);
}

/**
 * Query keys for comments
 * Used to manage cache invalidation and refetching
 */
export const commentsKeys = {
  all: ['comments'] as const,
  lists: () => [...commentsKeys.all, 'list'] as const,
  list: (
    organizationId: string,
    commentableType: CommentableEntityType,
    commentableId: string,
    filters: CommentFilters,
  ) => [...commentsKeys.lists(), organizationId, commentableType, commentableId, filters] as const,
  // Prefix for invalidating every list query for a given (org, commentable).
  // Keep this in sync with `list` so realtime invalidators don't drift from the key shape.
  listPrefix: (
    organizationId: string,
    commentableType: CommentableEntityType,
    commentableId: string,
  ) => [...commentsKeys.lists(), organizationId, commentableType, commentableId] as const,
  threadLists: () => [...commentsKeys.all, 'thread-list'] as const,
  threadList: (organizationId: string, emailIds: string[], filters: CommentFilters) => {
    const sortedEmailIds = [...emailIds].sort((a, b) => a.localeCompare(b));
    return [
      ...commentsKeys.threadLists(),
      organizationId,
      sortedEmailIds.join(','),
      filters,
    ] as const;
  },
  readReceipts: (
    organizationId: string,
    commentableType: CommentableEntityType,
    commentableId: string,
    threadRootCommentId?: string,
  ) =>
    [
      ...commentsKeys.all,
      'read-receipts',
      organizationId,
      commentableType,
      commentableId,
      threadRootCommentId ?? '',
    ] as const,
  readReceiptsPrefix: (organizationId: string) =>
    [...commentsKeys.all, 'read-receipts', organizationId] as const,
  // Batched read receipts query key. `sortedTargets` MUST be deduped and
  // sorted (`type` then `id`) before this is called so the key stays stable
  // across re-renders that hand the same logical target set in different
  // orders. The serialized join keeps the key cheap to compare deeply.
  readReceiptsBatch: (organizationId: string, sortedTargets: readonly BatchTarget[]) =>
    [
      ...commentsKeys.all,
      'read-receipts-batch',
      organizationId,
      sortedTargets.map(serializeBatchTarget).join('\n'),
    ] as const,
  readReceiptsBatchPrefix: (organizationId: string) =>
    [...commentsKeys.all, 'read-receipts-batch', organizationId] as const,
  details: () => [...commentsKeys.all, 'detail'] as const,
  detail: (id: string) => [...commentsKeys.details(), id] as const,
};
